// ABOUTME: Relation-portal service desk workscreen. Three-panel layout mirroring the warehouse view.
// ABOUTME: Reuses warehouse support-ticket hooks and chat components; backend scopes by relation_id header.

import { Box, useTheme } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from 'react-i18next';
import Subheading from "../../../../components/theme/text/Subheading";
import Shbutton from "../../../../components/theme/buttons/Shbutton";
import { tokens } from "../../../../theme";
import { ServicedeskTicketCardWarehouse } from "../../../warehouse/relation/service/components/ServicedeskTicketCard";
import TicketDetailPanel from "../../../warehouse/relation/service/components/TicketDetailPanel";
import ResolvedBanner from "../components/ResolvedBanner";
import MAddSupportTicket from "../components/modals/MAddSupportTicket";
import { useNewSupportTicketPrefill } from "../../../../utils/newSupportTicketPrefill";
import {
    useGetSupportTicketQuery,
    useGetRelationSupportTicketDashboardQuery,
} from "../../../../newapi/warehouse/supportTicketApiSlice";
import { Add } from "@mui/icons-material";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import Text from "../../../../components/theme/text/Text";

const gridOptions = {
    export: true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
};

// Compact date + time label for ticket cards, e.g. "7 Jul 14:30".
const formatUpdatedAt = (value) =>
    new Date(value).toLocaleString([], {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });

const RelationServiceDeskWorkScreen = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [selectedTicket, setSelectedTicket] = useState(null);
    const [isStuck, setIsStuck] = useState(false);
    const [addTicketOpen, setAddTicketOpen] = useState(false);
    // Arriving from a sidepanel "create support ticket" action opens the modal with that item linked.
    const { prefill: newTicketPrefill, clearPrefill: clearNewTicketPrefill } = useNewSupportTicketPrefill();
    useEffect(() => {
        if (newTicketPrefill) setAddTicketOpen(true);
    }, [newTicketPrefill]);
    const sentinelRef = useRef(null);

    const { data: dashboardData } = useGetRelationSupportTicketDashboardQuery(
        undefined,
        { pollingInterval: 30000 }
    );

    const dashboard = dashboardData?.data ?? {};
    const recentTickets = dashboard.recentTickets ?? [];

    // Kept solely so ResolvedBanner has the ticket data — TicketDetailPanel
    // dedupes the same query via RTK Query's cache.
    const { data: ticketDetails } = useGetSupportTicketQuery(selectedTicket?.id, {
        skip: !selectedTicket?.id
    });

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            ([entry]) => setIsStuck(!entry.isIntersecting),
            { threshold: 0, rootMargin: '-72px 0px 0px 0px' }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions: gridOptions,
            styles: {
                main: {
                    overflow: 'visible',
                },
            },
        });
    }, []);

    return (
        <Box sx={{
            display: "grid",
            gridTemplateColumns: "420px 1fr",
            "@media screen and (max-width: 63.9375em)": {
                gridTemplateColumns: "1fr",
            },
        }}>
            {/* Left - Ticket List */}
            <Box sx={{
                borderRight:`2px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                "@media screen and (max-width: 63.9375em)": {
                    maxHeight: 300,
                },
            }}>
                <Box sx={{ px: 4, pt: 4, pb: 1 }}>
                    <Shbutton
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        variant="contained"
                        endIcon={<Add/>}
                        onClick={() => setAddTicketOpen(true)}
                        className="block"
                    >
                        {t("new_ticket")}
                    </Shbutton>
                </Box>

                {recentTickets.length > 0 &&
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                pt: 3,
                                px: 4,
                                pb: 1,
                            }}
                        >
                            <Subheading>
                                {t("recent_tickets")}
                            </Subheading>
                        </Box>
                        <Box>
                            {recentTickets.map((ticket) => (
                                <ServicedeskTicketCardWarehouse
                                    key={ticket.id}
                                    ticket={{
                                        id: ticket.id,
                                        ticketNumber: ticket.ticketNumber,
                                        title: ticket.title,
                                        type: ticket.type,
                                        priority: ticket.priority?.toLowerCase(),
                                        // lastUpdateTime: new Date(ticket.updatedAt).toLocaleTimeString(),

                                        lastUpdateTime: formatUpdatedAt(ticket.updatedAt),
                                        description: ticket.description,
                                        updates: []
                                    }}
                                    onClick={() => setSelectedTicket(ticket)}
                                    selected={selectedTicket?.id === ticket.id}
                                />
                            ))}
                        </Box>
                    </>
                }
            </Box>

            {/* Main section wrapper - stretches to fill grid cell */}
            <Box sx={{alignSelf: 'stretch'}}>
                <Box ref={sentinelRef} sx={{ height: 0 }} />
                <Box
                    sx={{
                        display:'flex',

                        borderTopRightRadius:24,
                        position: "sticky",
                        top: 72,
                        height: isStuck ? "calc(100vh - 72px)" : "calc(100vh - 72px)",
                        transition: "height 0.2s ease",
                        "@media screen and (max-width: 63.9375em)": {
                            position: "static",
                            height: "auto",
                            maxHeight: "none",
                            overflow: "visible",
                        },
                    }}
                >
                    {!selectedTicket ? (
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 6,
                                textAlign: "center",
                                color: colors.txt["secondary"],
                            }}
                        >
                            <Box
                                sx={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: "50%",
                                    background:
                                        theme.palette.mode === "dark"
                                            ? colors.grey[400]
                                            : colors.grey[100],
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: colors.txt["primary"],
                                    mb:2,
                                }}
                            >
                                <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 36 }} />
                            </Box>
                            <Subheading>{t("no_ticket_selected")}</Subheading>
                            <Text align="center" light sx={{ maxWidth: 360 }}>
                                {t("select_ticket_to_view")}
                            </Text>
                        </Box>
                    ) : (
                        <TicketDetailPanel
                            ticketId={selectedTicket.id}
                            viewer="client"
                            banner={<ResolvedBanner ticket={ticketDetails?.data} />}
                        />
                    )}
                </Box>
            </Box>

            <MAddSupportTicket
                open={addTicketOpen}
                handleClose={() => { setAddTicketOpen(false); clearNewTicketPrefill(); }}
                initialLinkedItems={newTicketPrefill?.linkedItems}
            />
        </Box>
    );
};

export default RelationServiceDeskWorkScreen;
